import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { apiClient } from '../api/client';
import { hapticFeedback, isTelegramWebApp, getTelegramUser, openExternal } from '../utils/telegram';
import type { HairFaqRequest, FaceShape, HairType, ChatMessage } from '../types';
import { AiChatInput } from '../components/ui/AiChatInput';
import { AiPresetButton } from '../components/ui/AiPresetButton';
import { getShopProducts } from '../data/shopProducts';
import { aiPresets } from '../data/aiPresets';
import { YCLIENTS_BOOKING_URL } from '../constants';
import { useI18n } from '../i18n';

interface AiConsultationPageProps {
  initialFaceShape?: FaceShape;
  initialHairType?: HairType;
}

const cleanAiConsultationText = (text: string) =>
  text
    .replace(/\*\*/g, '')
    .replace(/^\s*\*\s+/gm, '')
    .replace(/\*/g, '')
    .replace(/^\s*#{1,6}\s+/gm, '')
    .replace(/^\s*step\s*\d+[\.:)]\s*/gim, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

export const AiConsultationPage: React.FC<AiConsultationPageProps> = ({
  initialFaceShape,
  initialHairType,
}) => {
  const { t, language } = useI18n();
  const presetTitle = (id: string) => t(({ 'curly-hair-care': 'presetCurly', 'face-shape-analysis': 'presetFace', 'hairstyle-recommendation': 'presetStyle', 'quick-styling': 'presetQuick', 'crop-styling': 'presetCrop' } as const)[id as 'curly-hair-care' | 'face-shape-analysis' | 'hairstyle-recommendation' | 'quick-styling' | 'crop-styling']);
  const answerLanguageInstruction = language === 'en'
    ? '\n\nAnswer in English.'
    : language === 'az'
      ? '\n\nAzərbaycan dilində cavab verin.'
      : '';
  const user = getTelegramUser();
  const userId = user?.id?.toString() || 'guest';
  
  const [sessionId] = useState(() => {
    const existing = sessionStorage.getItem('faq_session_id');
    if (existing) return existing;
    const newSessionId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('faq_session_id', newSessionId);
    return newSessionId;
  });
  
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    try {
      const stored = localStorage.getItem(`faq_messages_${userId}`);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [attachedImages, setAttachedImages] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [faceShapeAnalysis, setFaceShapeAnalysis] = useState<string | null>(() => {
    try {
      return localStorage.getItem(`face_shape_${userId}`);
    } catch {
      return null;
    }
  });
  const [activePresetPrompt, setActivePresetPrompt] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    try {
      localStorage.setItem(`faq_messages_${userId}`, JSON.stringify(messages));
    } catch (error) {
      console.error('Failed to save messages:', error);
    }
  }, [messages, userId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleImageAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const newFiles = Array.from(files).slice(0, 3 - attachedImages.length);
    
    newFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachedImages(prev => [...prev, file]);
        setImagePreviewUrls(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
    
    if (e.target) {
      e.target.value = '';
    }
  };
  
  const handleRemoveImage = (index: number) => {
    setAttachedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const clearMemory = async () => {
    if (!userId) return;
    try {
      await apiClient.clearFaqMemory(userId);
      setMessages([]);
      setFaceShapeAnalysis(null);
      setActivePresetPrompt(null);
      localStorage.removeItem(`faq_messages_${userId}`);
      localStorage.removeItem(`face_shape_${userId}`);
      toast.success(t('memoryCleared'));
      hapticFeedback('medium');
    } catch (error) {
      console.error('Error clearing memory:', error);
      toast.error(t('clearError'));
    }
  };

  const handlePresetClick = async (presetId: string) => {
    const preset = aiPresets.find(p => p.id === presetId);
    if (!preset) return;

    hapticFeedback('medium');
    setActivePresetPrompt(preset.systemPrompt);

    if (preset.requiresPhoto) {
      if (preset.initialMessage) {
        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
        text: t('uploadPhoto'),
          createdAt: Date.now()
        };
        setMessages([assistantMessage]);
      }
    } else {
      if (preset.initialMessage) {
        await handleSend(presetTitle(preset.id));
      }
    }
  };

  const handleSend = async (question: string) => {
    if (!question.trim() && attachedImages.length === 0) return;
    if (isLoading) return;
    
    const hasImages = attachedImages.length > 0;
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
        text: question || (hasImages ? `📷 ${t('photoAnalysis')}` : ''),
      createdAt: Date.now(),
      images: hasImages ? imagePreviewUrls : undefined
    };
    
    const thinkingMessage: ChatMessage = {
      id: `thinking-${Date.now()}`,
      role: 'assistant-thinking',
      text: '',
      createdAt: Date.now()
    };
    
    setMessages((prev) => [...prev, userMessage, thinkingMessage]);
    setIsLoading(true);
    if (isTelegramWebApp()) hapticFeedback('medium');
    
    const imagesToSend = [...attachedImages];
    setAttachedImages([]);
    setImagePreviewUrls([]);
    
    try {
      let response;
      const recentMessages = messages.slice(-16).map(m => ({ role: m.role, content: m.text }));
      const shopContext = getShopProducts(t).map(p => `${p.name} — ${p.brand} (${p.category}): ${p.shortDescription}; ${t('shopFrom', { price: p.priceFrom })}`).join('\n');
      
      const prompt = `${activePresetPrompt ? `${activePresetPrompt}\n\n` : ''}${question}\n\nConversation context: ${JSON.stringify(recentMessages)}\n\nAvailable products:\n${shopContext}${faceShapeAnalysis ? `\n\nKnown face shape: ${faceShapeAnalysis}` : ''}${answerLanguageInstruction}`;

      if (hasImages) {
        response = await apiClient.askHairFaqWithImages({
          question: prompt,
          face_shape: initialFaceShape || (faceShapeAnalysis as FaceShape | undefined),
          hair_type: initialHairType,
          user_id: userId,
          session_id: sessionId,
          images: imagesToSend
        });
        
        if (imagesToSend.length > 0) {
          try {
            const faceResult = await apiClient.detectFaceShape(imagesToSend[0]);
            setFaceShapeAnalysis(faceResult.face_shape);
            localStorage.setItem(`face_shape_${userId}`, faceResult.face_shape);
          } catch (error) {
            console.error('Face shape detection failed:', error);
          }
        }
      } else {
        const payload: HairFaqRequest = {
          question: prompt,
          face_shape: initialFaceShape || (faceShapeAnalysis as FaceShape | undefined),
          hair_type: initialHairType,
          user_id: userId,
          session_id: sessionId
        };
        response = await apiClient.askHairFaq(payload);
      }
      
      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        text: response.answer,
        createdAt: Date.now()
      };
      
      setMessages((prev) => {
        const withoutThinking = prev.filter(m => m.role !== 'assistant-thinking');
        return [...withoutThinking, assistantMessage];
      });
    } catch (error: unknown) {
      console.error('Error asking FAQ:', error);
      const err = error as { detail?: string; message?: string };
      toast.error(t('answerError'));
      
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        text: t('aiErrorMessage'),
        createdAt: Date.now()
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full" style={{ 
      background: '#0B0B0E'
    }}>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
        className="px-4 pt-4 pb-2"
      >
        <h1 className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.7)' }}>
          {t('aiConsultation')}
        </h1>
      </motion.div>
      {messages.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center justify-end px-4 pb-2"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={clearMemory}
            className="text-xs text-white/35 hover:text-white/60 transition-colors"
          >
            {t('clear')}
          </motion.button>
        </motion.div>
      )}
      
      {messages.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          className="px-4 pb-4 space-y-3"
        >
          <p className="text-xs font-medium uppercase tracking-wider mb-4" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {t('quickStart')}
          </p>
          {aiPresets.map((preset, index) => (
            <motion.div
              key={preset.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.3, 
                delay: index * 0.08,
                ease: [0.25, 0.1, 0.25, 1] 
              }}
            >
              <AiPresetButton
                title={presetTitle(preset.id)}
                onClick={() => handlePresetClick(preset.id)}
                disabled={isLoading}
              />
            </motion.div>
          ))}
        </motion.div>
      )}
      
      <div className="flex-1 overflow-y-auto px-4 pb-4 hide-scrollbar">
        <AnimatePresence initial={false}>
          {messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
              className={`flex mb-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {message.role === 'assistant-thinking' ? (
                <div
                  className="max-w-[85%] px-4 py-3 rounded-[18px] flex items-center gap-1.5"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    backdropFilter: 'blur(30px) saturate(150%)',
                    WebkitBackdropFilter: 'blur(30px) saturate(150%)',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)'
                  }}
                >
                  <motion.span
                    animate={{ opacity: [0.25, 0.7, 0.25] }}
                    transition={{ duration: 1.4, repeat: Infinity, delay: 0, ease: 'easeInOut' }}
                    className="w-1.5 h-1.5 bg-white/40 rounded-full"
                  />
                  <motion.span
                    animate={{ opacity: [0.25, 0.7, 0.25] }}
                    transition={{ duration: 1.4, repeat: Infinity, delay: 0.2, ease: 'easeInOut' }}
                    className="w-1.5 h-1.5 bg-white/40 rounded-full"
                  />
                  <motion.span
                    animate={{ opacity: [0.25, 0.7, 0.25] }}
                    transition={{ duration: 1.4, repeat: Infinity, delay: 0.4, ease: 'easeInOut' }}
                    className="w-1.5 h-1.5 bg-white/40 rounded-full"
                  />
                </div>
              ) : (
                <div
                  className={`max-w-[85%] px-4 py-3 text-[15px] leading-relaxed ${
                    message.role === 'user'
                      ? 'text-white/95 rounded-[18px] font-normal'
                      : 'text-white/90 rounded-[18px]'
                  }`}
                  style={{
                    background: message.role === 'user'
                      ? 'linear-gradient(145deg, rgba(28,28,30,0.95) 0%, rgba(22,22,24,0.98) 100%)'
                      : 'rgba(255,255,255,0.06)',
                    backdropFilter: message.role === 'user' ? 'blur(20px)' : 'blur(30px) saturate(150%)',
                    WebkitBackdropFilter: message.role === 'user' ? 'blur(20px)' : 'blur(30px) saturate(150%)',
                    border: 'none',
                    boxShadow: message.role === 'user'
                      ? '0 2px 12px rgba(0,0,0,0.35), inset 0 2px 0 rgba(255,255,255,0.05), inset 0 -2px 4px rgba(0,0,0,0.3)'
                      : '0 2px 12px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)'
                  }}
                >
                  {message.role === 'user' && message.images && message.images.length > 0 && (
                    <div className="flex gap-2 mb-2">
                      {message.images.map((url, idx) => (
                        <img
                          key={idx}
                          src={url}
                          alt={t('attachmentAlt', { count: idx + 1 })}
                          className="w-16 h-16 object-cover rounded-lg"
                          style={{ border: '1px solid rgba(0,0,0,0.08)' }}
                        />
                      ))}
                    </div>
                  )}
                  <p className="whitespace-pre-wrap">
                    {message.role === 'assistant' ? cleanAiConsultationText(message.text) : message.text}
                  </p>
                  {message.role === 'assistant' && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                      className="mt-3 flex flex-wrap gap-3 pt-2"
                      style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
                    >
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          hapticFeedback('light');
                          openExternal(YCLIENTS_BOOKING_URL);
                        }}
                        className="text-[10px] font-medium uppercase tracking-wider text-white/50 hover:text-white/80 transition-all duration-200"
                      >
                        {t('book')}
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          hapticFeedback('light');
                          window.dispatchEvent(new CustomEvent('openShop'));
                        }}
                        className="text-[10px] font-medium uppercase tracking-wider text-white/50 hover:text-white/80 transition-all duration-200"
                      >
                        {t('openShop')}
                      </motion.button>
                    </motion.div>
                  )}
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
        <div ref={messagesEndRef} />
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        onChange={handleImageAttach}
        className="sr-only"
        style={{ position: 'absolute', left: '-9999px' }}
      />
      
      <div className="px-4 pb-4">
        <AiChatInput
          onSend={handleSend}
          isLoading={isLoading}
          placeholder={t('yourQuestion')}
          allowImageAttach={true}
          onImageAttach={() => fileInputRef.current?.click()}
          attachedImagesCount={attachedImages.length}
          imagePreviewUrls={imagePreviewUrls}
          onRemoveImage={handleRemoveImage}
        />
      </div>
    </div>
  );
};
